/**
 * AgentsAllTestPage — D-1. 에이전트 전체보기 (테스트용 정적 화면)
 *
 * ⚠️ 테스트/목업 전용. 기존 AgentsPage.tsx(API 연동본)와 별개다.
 *   - API 연동 없음 — 구독/출시예정 데이터는 이 파일의 상수 목업 사용.
 *   - 레이아웃·디자인 토큰은 AgentsPage.tsx와 동일(D-08 §6.4 groupTheme 포함).
 *   - 화면기획서 studio-agent-all-web.html 기준 카드 문구 반영.
 *
 * 섹션 구성:
 *   1) 구독 중인 에이전트 — [열기] 이동, [사용가이드] 모달(정적)
 *   2) 출시 예정 에이전트 — [알림 받기] → 로컬 상태 토글
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Badge, Button, Card, Modal } from '@indipia/ui-core';
import { AGENT_MENU_CONFIG } from '../../registry/menu-config';

// ── 목업 데이터 ────────────────────────────────────────────────────────────────

interface MockSubscription {
  agentSubId: number;
  agentId: number;       // numericAgentId — 메뉴 이동 매핑용
  agentName: string;
  recommendTitle: string;
}

interface MockUpcoming {
  agentId: number;
  agentName: string;
  description: string;
}

const MOCK_SUBSCRIPTIONS: MockSubscription[] = [
  { agentSubId: 1, agentId: 1, agentName: '인디포토',       recommendTitle: '현장사진, 이제는 찍으면 알아서 정리됩니다' },
  { agentSubId: 2, agentId: 8, agentName: '인디공사스케줄', recommendTitle: '이제는, 엑셀 견적서로 공정표를 자동으로 만들고, 효율적으로 마감일을 관리해 보세요.' },
  { agentSubId: 3, agentId: 2, agentName: '인디공사현황',   recommendTitle: '하루 공사 현황을 자동으로 정리해 공유합니다.' },
];

const MOCK_UPCOMING: MockUpcoming[] = [
  { agentId: 101, agentName: '인디물량산출기', description: '이제는, 평면도 이미지로 쉽게 물량을 산출해 보세요.' },
  { agentId: 102, agentName: '인디견적작성기', description: '이제는, 고객 전송용 견적서 쉽게 만들어 보세요.' },
  { agentId: 103, agentName: '인디캘린더',     description: '이제는, 여러 고객과 현장 일정을 전문 캘린더 하나로 관리해보세요.' },
];

// ── 에이전트 시각 매핑 ────────────────────────────────────────────────────────
// group: D-08 §6.4 groupTheme 스코프 ('01'=사진, '02'=현황, '03'=스케줄) — AgentsPage.tsx와 동일

const AGENT_VISUAL: Record<number, { group: string; icon: React.ReactNode }> = {
  1: {
    group: '01',
    icon: (
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
        <circle cx="12" cy="13" r="4"/>
      </svg>
    ),
  },
  2: {
    group: '03',
    icon: (
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
        <line x1="8" y1="14" x2="8.01" y2="14" strokeWidth="2.5"/><line x1="12" y1="14" x2="12.01" y2="14" strokeWidth="2.5"/>
        <line x1="16" y1="14" x2="16.01" y2="14" strokeWidth="2.5"/><line x1="8" y1="18" x2="8.01" y2="18" strokeWidth="2.5"/>
        <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.5"/>
      </svg>
    ),
  },
  8: {
    group: '02',
    icon: (
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
  },
};

// ── 사용 가이드 모달 (정적) ─────────────────────────────────────────────────────

function GuideModal({ agentName, onClose }: { agentName: string; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={`${agentName} 사용 가이드`}>
      <div className="py-12 text-center">
        테스트 화면입니다. 실제 사용 가이드는 API 연동 후 표시됩니다.
      </div>
    </Modal>
  );
}

// ── 메인 페이지 ────────────────────────────────────────────────────────────────

export function AgentsAllTestPage() {
  const navigate = useNavigate();

  const [guideAgent, setGuideAgent] = useState<string | null>(null);
  const [notified, setNotified] = useState<Set<number>>(new Set());

  const menuMap = new Map(AGENT_MENU_CONFIG.map((c) => [c.numericAgentId, c]));

  return (
    <div className="mx-auto max-w-3xl space-y-12 p-8">

      {/* ── 1. 구독 중인 에이전트 ──────────────────────────────────────── */}
      <section>
        <div className="mb-6">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-widest text-point">
            구독 중인 에이전트
          </p>
          <h2 className="text-lg font-extrabold tracking-tight text-text">내 스튜디오 에이전트</h2>
          <p className="mt-1 text-xs text-text-muted">
            현재 {MOCK_SUBSCRIPTIONS.length}개 AI 에이전트가 활성화되어 있습니다
          </p>
        </div>

        <div className="space-y-3">
          {MOCK_SUBSCRIPTIONS.map((sub) => {
            const visual = AGENT_VISUAL[sub.agentId];
            const menuConf = menuMap.get(sub.agentId);
            return (
              <Card
                key={sub.agentSubId}
                data-group={visual?.group}
                className="transition-colors hover:border-point"
              >
                <div className="flex items-center gap-5">
                  {/* 아이콘 패널 */}
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md bg-group-subtle text-group">
                    {visual?.icon ?? (
                      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7" rx="1.5"/>
                        <rect x="14" y="3" width="7" height="7" rx="1.5"/>
                        <rect x="3" y="14" width="7" height="7" rx="1.5"/>
                        <rect x="14" y="14" width="7" height="7" rx="1.5"/>
                      </svg>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-base font-extrabold tracking-tight text-text">{sub.agentName}</p>
                    {sub.recommendTitle && (
                      <p className="mt-1 text-xs leading-relaxed text-text-muted">{sub.recommendTitle}</p>
                    )}
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <Badge status="success">구독중</Badge>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => menuConf && navigate(menuConf.defaultPath)}
                      disabled={!menuConf}
                    >
                      열기
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setGuideAgent(sub.agentName)}
                    >
                      사용가이드 →
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* ── 2. 출시 예정 에이전트 ─────────────────────────────────────── */}
      <section>
        <div className="mb-6">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-widest text-point">
            출시 예정 에이전트
          </p>
          <h2 className="text-lg font-extrabold tracking-tight text-text">곧 만나볼 수 있어요</h2>
          <p className="mt-1 text-xs text-text-muted">
            더 많은 AI 에이전트가 준비 중입니다. 알림을 받고 가장 먼저 사용해 보세요
          </p>
        </div>

        <div className="space-y-2.5">
          {MOCK_UPCOMING.map((agent) => {
            const isNotified = notified.has(agent.agentId);
            return (
              <Card key={agent.agentId}>
                <div className="flex items-center gap-4">
                  {/* 아이콘 패널 */}
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-surface-muted">
                    <svg
                      width="22" height="22" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
                      className="text-text-muted"
                    >
                      <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                      <polyline points="2 17 12 22 22 17"/>
                      <polyline points="2 12 12 17 22 12"/>
                    </svg>
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold tracking-tight text-text-secondary">{agent.agentName}</p>
                    <p className="mt-1 text-xs leading-relaxed text-text-muted">{agent.description}</p>
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <Badge variant="outline">출시예정</Badge>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={isNotified}
                      onClick={() =>
                        setNotified((prev) => {
                          const next = new Set(prev);
                          next.add(agent.agentId);
                          return next;
                        })
                      }
                    >
                      {isNotified ? '예약 완료' : '알림 받기'}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* ── 사용 가이드 모달 ──────────────────────────────────────────── */}
      {guideAgent !== null && (
        <GuideModal agentName={guideAgent} onClose={() => setGuideAgent(null)} />
      )}
    </div>
  );
}
